import { offsetByTicks } from "./priceUtils.js";

/**
 * Build OTO bracket legs (stop-loss + take-profit) for a main order.
 *
 * Pure function — no store or marketdata side effects beyond priceUtils.
 * Prices are derived via offsetByTicks which calls normalizePrice internally.
 *
 * @param {Object} params
 * @param {string} params.mainOrderId  - ID of the parent order (used to build leg IDs)
 * @param {string} params.symbol       - Translated contract symbol (e.g. WING26, not WINFUT)
 * @param {string} params.side         - 'buy' | 'sell' — side of the MAIN order
 * @param {number} params.quantity     - Quantity for each leg (matches main order)
 * @param {number} params.anchorPrice  - Price from which distances are measured
 * @param {number} params.lossTicks    - Positive integer: stop-loss distance in ticks
 * @param {number} params.profitTicks  - Positive integer: take-profit distance in ticks
 * @returns {Array} [stopLeg, profitLeg] overlay-shaped objects, or [] if inputs invalid
 */
export const buildOtoLegs = ({
  mainOrderId,
  symbol,
  side,
  quantity,
  anchorPrice,
  lossTicks,
  profitTicks,
}) => {
  if (!mainOrderId || !symbol) return [];
  if (!isFinite(anchorPrice) || anchorPrice <= 0) return [];
  if (side !== "buy" && side !== "sell") return [];
  if (!quantity || quantity <= 0) return [];
  if (!lossTicks || lossTicks < 1 || !profitTicks || profitTicks < 1) return [];

  const oppositeSide = side === "buy" ? "sell" : "buy";

  // Buy: stop below anchor (negative offset), profit above (positive)
  // Sell: stop above anchor (positive offset), profit below (negative)
  const stopTickOffset =
    side === "buy" ? -Math.round(lossTicks) : Math.round(lossTicks);
  const profitTickOffset =
    side === "buy" ? Math.round(profitTicks) : -Math.round(profitTicks);

  const stopPrice = offsetByTicks(symbol, anchorPrice, stopTickOffset);
  const profitPrice = offsetByTicks(symbol, anchorPrice, profitTickOffset);

  const stopLeg = {
    id: `${mainOrderId}-S`,
    type: "stop",
    orderType: "STOPLIMIT",
    price: stopPrice,
    quantity,
    symbol,
    side: oppositeSide,
    state: "local",
    deltaTicks: stopTickOffset,
  };

  const profitLeg = {
    id: `${mainOrderId}-P`,
    type: "profit",
    orderType: "LIMIT",
    price: profitPrice,
    quantity,
    symbol,
    side: oppositeSide,
    state: "local",
    deltaTicks: profitTickOffset,
  };

  return [stopLeg, profitLeg];
};
